/**
 * Глобальное состояние приложения сотрудника
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export interface WorkerState {
  // Данные сотрудника
  employeeId: string | null
  fullName: string | null
  role: 'worker' | 'admin' | null
  shortId: number | null // Компактный ID для binary QR токенов
  totpSecret: string | null // TOTP secret для оффлайн генерации QR
  
  // Приватный ключ (в зашифрованном виде)
  encryptedPrivateKey: string | null
  
  // Статус онбординга
  isOnboarded: boolean
  
  // Действия
  setEmployeeData: (employeeId: string, fullName: string, role: 'worker' | 'admin', shortId?: number) => void
  setEncryptedPrivateKey: (encryptedKey: string) => void
  setOnboarded: (onboarded: boolean) => void
  setShortId: (shortId: number) => void
  setTotpSecret: (secret: string) => void
  reset: () => void
}

const initialState = {
  employeeId: null,
  fullName: null,
  role: null,
  shortId: null,
  totpSecret: null,
  encryptedPrivateKey: null,
  isOnboarded: false,
}

// Функция для безопасного получения storage
function getStorage() {
  try {
    // Проверяем доступность localStorage
    const test = '__localStorage_test__'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return localStorage
  } catch {
    // Если localStorage недоступен (приватный режим), используем sessionStorage
    try {
      const test = '__sessionStorage_test__'
      sessionStorage.setItem(test, test)
      sessionStorage.removeItem(test)
      return sessionStorage
    } catch {
      // Если и sessionStorage недоступен, используем in-memory storage
      const memoryStorage: Record<string, string> = {}
      return {
        getItem: (key: string) => memoryStorage[key] || null,
        setItem: (key: string, value: string) => { memoryStorage[key] = value },
        removeItem: (key: string) => { delete memoryStorage[key] },
        clear: () => { Object.keys(memoryStorage).forEach(k => delete memoryStorage[k]) },
        length: Object.keys(memoryStorage).length,
        key: (index: number) => Object.keys(memoryStorage)[index] || null
      } as Storage
    }
  }
}

export const useWorkerStore = create<WorkerState>()(
  persist(
    (set) => ({
      ...initialState,
      
      setEmployeeData: (employeeId, fullName, role, shortId) => {
        // Валидация role
        if (role !== 'worker' && role !== 'admin') {
          console.error('Invalid role:', role)
          return
        }
        set({ employeeId, fullName, role, shortId: shortId || null })
      },
      
      setShortId: (shortId) => {
        set({ shortId })
      },
      
      setTotpSecret: (secret) => {
        if (!secret || typeof secret !== 'string') {
          console.error('Invalid TOTP secret format')
          return
        }
        set({ totpSecret: secret })
      },
      
      setEncryptedPrivateKey: (encryptedKey) => {
        if (!encryptedKey || typeof encryptedKey !== 'string') {
          console.error('Invalid encrypted key format')
          return
        }
        set({ encryptedPrivateKey: encryptedKey })
      },
      
      setOnboarded: (onboarded) => {
        set({ isOnboarded: onboarded })
      },
      
      reset: () => {
        // Очищаем push-уведомления при сбросе
        try {
          // Динамический импорт чтобы избежать циклических зависимостей
          import('../services/pushNotificationService').then(({ PushNotificationService }) => {
            PushNotificationService.clearSavedData()
            console.log('[WorkerStore] Push notification data cleared on reset')
          })
        } catch (error) {
          console.error('[WorkerStore] Failed to clear push data:', error)
        }
        
        set(initialState)
      },
    }),
    {
      name: 'worker-storage',
      storage: createJSONStorage(() => getStorage()),
      // Не сохраняем зашифрованный ключ в localStorage напрямую
      // В будущем можно использовать более безопасное хранилище
    }
  )
)

